import { Popover } from "frost-ui-react";
import { PopoverProps } from "frost-ui-react/dist/Popover/Popover";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { APPLICATION_STATUS } from "../../../configs/constants/ApplicationStatus";
import { routes } from "../../../configs/constants/routes";
import { useJob } from "../../../networking/job.hooks";
import { formatDate, shiftTimeFromTo } from "../../../utils/date.time.utils";
import { consultantRoute, customerRoute, formatRoute, jobRoute } from "../../../utils/route.utils";
import AppText, { FormLabel, LinkText } from "../../Core/AppText/AppText";
import Flex from "../../Layout/Flex";
import Divider from "../Divider/Divider";
import JobStatusTag from "../JobStatusTag";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import { ScheduleEvent } from "./Schedule";
import dayjs from "dayjs";

interface Props {
	scheduleEvent: ScheduleEvent;
	pointTo: PopoverProps["pointTo"];
	onClose: () => any;
}

const JobPopover: React.FC<Props> = ({ scheduleEvent, pointTo, onClose }) => {
	if (!scheduleEvent.shift && !scheduleEvent.job) return null;

	const { t } = useTranslation();

	const { job, isLoading } = useJob({
		jobId: scheduleEvent?.job?.id,
		expand: ["applications", "customer"],
	});

	const bookedApplication = (job?.applications || []).find(
		(application) =>
			application.status === APPLICATION_STATUS.ACCEPTED &&
			application.shifts?.find((s) => s.id === scheduleEvent.shift?.id)
	);

	function renderBody() {
		return (
			<>
				<Flex direction="column" gap="2px">
					<FormLabel margin="0">{t("JOB")}</FormLabel>
					<LinkText size="s" href={jobRoute(job?.id || "")}>
						#{job?.orderNumber}
					</LinkText>
				</Flex>

				{bookedApplication && (
					<Flex direction="column" gap="2px">
						<FormLabel margin="0">{t("CONSULTANT")}</FormLabel>
						<LinkText
							data-contact={bookedApplication.consultant.id}
							data-date={scheduleEvent.start ? dayjs(scheduleEvent.start).format("YYYY-MM-DD") : ""}
							href={consultantRoute(bookedApplication.consultant.id)}
						>
							{bookedApplication.consultant.firstName} {bookedApplication.consultant.lastName}
						</LinkText>
					</Flex>
				)}
				<Flex direction="column" gap="2px">
					<FormLabel margin="0">{t("CUSTOMER")}</FormLabel>
					<LinkText size="s" href={customerRoute(job?.customer.id || "")}>
						{job?.customer.name}
					</LinkText>
				</Flex>
				<Flex direction="column" gap="2px">
					<FormLabel margin="0">{t("CONTACT_PERSON")}</FormLabel>
					<LinkText
						size="s"
						href={formatRoute([routes.CUSTOMER_DETAILS, routes.CUSTOMER_USERS].join("/"), [
							job?.customer.id,
						])}
					>
						{job?.contactPersons[0].firstName} {job?.contactPersons[0].lastName}
					</LinkText>
				</Flex>
			</>
		);
	}

	return (
		<Popover pointTo={pointTo} styles={{ width: 350 }}>
			<Styles>
				<Flex gap="1rem" direction="column">
					<Flex justifyContent="space-between" alignItems="flex-start">
						<Flex direction="column" gap={"2px"}>
							<AppText size="s" color="grayTextDark">
								{formatDate(scheduleEvent.start)}
							</AppText>
							<AppText weight="medium">{shiftTimeFromTo(scheduleEvent)}</AppText>
							{scheduleEvent.type === "shift-extend-requested" && (
								<AppText italic color="orange" size="s">
									Arbetspasset ej bekräftat. Kund önskar förlänga och inväntar svar.
								</AppText>
							)}
							{scheduleEvent.type === "shift-extend-declined" && (
								<AppText italic color="danger" size="s">
									Kund önskade förlänga men arbetspasset har nekats.
								</AppText>
							)}
						</Flex>
						<JobStatusTag size="s" job={job} />
					</Flex>
					<Divider margin="0" width={1} />

					{isLoading ? <LoadingIndicator /> : renderBody()}

					<LinkText onClick={onClose} color="danger" textAlign="center">
						{t("CLOSE")} (Esc)
					</LinkText>
				</Flex>
			</Styles>
		</Popover>
	);
};

export default JobPopover;

const Styles = styled.div`
	.profile-pic {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		border: 2px solid black;
		background: #ccc;
	}
`;
